/**
 * Floor annotation properties — registration and getters.
 *
 * Properties registered here:
 *
 * Geometry (PROJECT_GLOBAL, COORDINATE) — written by floor detection tools,
 * consumed by FloorplanPersona visualization and IFC export. Not user-facing.
 * - floor_plane_position
 * - floor_plane_normal
 * - ceiling_plane_position
 * - ceiling_plane_normal
 * - floor_footprint_corner_0..3 — the floor's OWN XY footprint (4 corners,
 *   supports rotated rectangles). Used for per-floor visualization sizing and
 *   box clipping instead of the project-global detection area.
 *
 * 3D mesh visualization + heatmap + clipping (PROJECT_GLOBAL) — the floor
 * owns the consolidated user-facing controls; `floor_wall_source` picks
 * whether they drive the wall sketch or the rooms-based mesh (outline ⊖
 * rooms) as the floor's wall geometry.
 * - floor_wall_source
 * - floor_mesh3d_enabled / _color / _opacity
 * - floor_heatmap_enabled / _gradient / _max_distance / _height_tolerance
 *   / _clip_enabled / _clip_method / _clip_tolerance
 */

import { AnnotationType, PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';

/** ConvertType.DISTANCE (client enum): shown in the user's length unit, stored in meters. */
const CONVERT_TYPE_DISTANCE = 1;

type Coordinate = { x: number; y: number; z: number };

export function registerFloorProperties() {
  if (!propertyRegistry.isRegistered('floor_plane_position')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_plane_position',
      name: 'Floor Plane Position',
      valueType: PropertyValueType.COORDINATE,
      defaultValue: { x: 0, y: 0, z: 0 },
      annotationTypes: [AnnotationType.FLOOR],
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('floor_plane_normal')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_plane_normal',
      name: 'Floor Plane Normal',
      valueType: PropertyValueType.COORDINATE,
      defaultValue: { x: 0, y: 0, z: 1 },
      annotationTypes: [AnnotationType.FLOOR],
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('ceiling_plane_position')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'ceiling_plane_position',
      name: 'Ceiling Plane Position',
      valueType: PropertyValueType.COORDINATE,
      defaultValue: { x: 0, y: 0, z: 0 },
      annotationTypes: [AnnotationType.FLOOR],
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('ceiling_plane_normal')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'ceiling_plane_normal',
      name: 'Ceiling Plane Normal',
      valueType: PropertyValueType.COORDINATE,
      defaultValue: { x: 0, y: 0, z: 1 },
      annotationTypes: [AnnotationType.FLOOR],
      ui: { hidden: true },
    });
  }

  for (let i = 0; i < 4; i++) {
    const id = `floor_footprint_corner_${i}`;
    if (!propertyRegistry.isRegistered(id)) {
      propertyRegistry.register({
        type: PropertyType.PROJECT_GLOBAL,
        identifier: id,
        name: `Floor Footprint Corner ${i}`,
        valueType: PropertyValueType.COORDINATE,
        defaultValue: { x: 0, y: 0, z: 0 },
        annotationTypes: [AnnotationType.FLOOR],
        description: `Corner ${i} of the floor's own XY footprint`,
        ui: { hidden: true },
      });
    }
  }

  // ─── 3D mesh visualization ──────────────────────────────────────────
  if (!propertyRegistry.isRegistered('floor_mesh3d_enabled')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_mesh3d_enabled',
      name: '3D Mesh',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: true,
      annotationTypes: [AnnotationType.FLOOR],
      description: 'Extrude the floor outline (minus rooms) into a 3D wall mesh',
      ui: {
        tooltip: 'Build a 3D wall mesh from the floor outline by stamping out room polygons and extruding the remaining shell to ceiling height. Door and window openings are cut afterwards.',
        group: '3D Mesh',
        order: 0,
        isGroupToggle: true,
      },
    });
  }

  if (!propertyRegistry.isRegistered('floor_mesh3d_color')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_mesh3d_color',
      name: 'Wall Color',
      valueType: PropertyValueType.COLOR,
      defaultValue: '#808080',
      annotationTypes: [AnnotationType.FLOOR],
      description: 'Color of the extruded wall mass',
      ui: {
        tooltip: 'Color used for the wall shell. Individual rooms inherit their own annotation color.',
        group: '3D Mesh',
        order: 1,
      },
    });
  }

  if (!propertyRegistry.isRegistered('floor_mesh3d_opacity')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_mesh3d_opacity',
      name: 'Opacity',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0.7,
      annotationTypes: [AnnotationType.FLOOR],
      description: 'Opacity of the 3D mesh',
      validation: { min: 0.0, max: 1.0, step: 0.05 },
      ui: { group: '3D Mesh', order: 2 },
    });
  }

  // ─── Wall source (rooms vs wall sketch) ─────────────────────────────
  // Which wall geometry represents this floor: the WALL SKETCH (mitered
  // skeleton wall outlines) or the ROOMS-based mesh (outline ⊖ rooms). One
  // consolidated set of floor controls — the 3D mesh toggle, the heatmap and
  // the point cloud clipping below — drives whichever source is picked; the
  // wall sketch has no independent 3D/heatmap/clipping settings anymore.
  if (!propertyRegistry.isRegistered('floor_wall_source')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_wall_source',
      name: 'source',
      valueType: PropertyValueType.SELECT,
      defaultValue: 'WALL_SKETCH',
      annotationTypes: [AnnotationType.FLOOR],
      description: 'Wall representation of the floor: also what the 3D mesh, heatmap and point cloud clipping work off',
      validation: {
        options: [
          // Data values stay WALL_SKETCH / ROOMS (stored in existing
          // projects); the labels match the Wall tab's representation prompt
          // (Standardised / As-built — the AEC term for as-measured reality).
          { value: 'WALL_SKETCH', label: 'standardised' },
          { value: 'ROOMS', label: 'as-built' },
        ],
      },
      // groupOrder 0 floats the Source section above the other floor groups
      // (they default to 999) — the dropdown reads as a mode switch for
      // everything below it.
      ui: { tooltip: 'Show the standardised walls, or the as-built view (skin outline + skeleton with error highlighting); 3D mesh, heatmap and wall distance follow the same source', group: 'Source', groupOrder: 0, order: 0 },
    });
  }

  // ─── Heatmap (mirrors skeleton SDF options) ─────────────────────────
  if (!propertyRegistry.isRegistered('floor_heatmap_enabled')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_heatmap_enabled',
      name: 'Wall Distance Heatmap',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.FLOOR],
      description: 'Color the point cloud based on distance to the floor wall mesh',
      ui: {
        tooltip: 'Apply gradient coloring to points based on distance to the floor wall mesh.',
        group: 'Heatmap',
        order: 0,
        isGroupToggle: true,
      },
    });
  }

  if (!propertyRegistry.isRegistered('floor_heatmap_gradient')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_heatmap_gradient',
      name: 'Gradient Type',
      valueType: PropertyValueType.GRADIENT,
      defaultValue: 'SPECTRAL',
      annotationTypes: [AnnotationType.FLOOR],
      description: 'Gradient type for distance coloring',
      ui: { group: 'Heatmap', order: 1 },
    });
  }

  if (!propertyRegistry.isRegistered('floor_heatmap_max_distance')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_heatmap_max_distance',
      name: 'Max Distance',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0.02,
      annotationTypes: [AnnotationType.FLOOR],
      description: 'Maximum distance for gradient (meters)',
      validation: { min: 0.001, max: 50.0, step: 0.01 },
      ui: { tooltip: 'Maximum distance from walls for gradient coloring', group: 'Heatmap', order: 2, decimals: 3, useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE },
    });
  }

  if (!propertyRegistry.isRegistered('floor_heatmap_height_tolerance')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_heatmap_height_tolerance',
      name: 'Height Range',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 2.0,
      annotationTypes: [AnnotationType.FLOOR],
      description: 'How far above/below floor to apply effects',
      validation: { min: 0.1, max: 10.0, step: 0.1 },
      // Hidden — the heatmap/clipping band is derived from the floor's own
      // height range (floor → ceiling planes), not a user-set tolerance.
      // Registration kept so stored project data doesn't orphan.
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('floor_heatmap_clip_enabled')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_heatmap_clip_enabled',
      name: 'Point Cloud Clipping',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.FLOOR],
      description: 'Enable point cloud clipping based on distance to walls',
      ui: { tooltip: 'Enable clipping of points based on distance to the floor wall mesh.', group: 'Point Cloud Clipping', order: 0, isGroupToggle: true },
    });
  }

  if (!propertyRegistry.isRegistered('floor_heatmap_clip_method')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_heatmap_clip_method',
      name: 'Clip Method',
      valueType: PropertyValueType.SELECT,
      defaultValue: 'SHOW_INSIDE',
      annotationTypes: [AnnotationType.FLOOR],
      description: 'Clip points based on distance to walls',
      validation: {
        options: [
          { value: 'SHOW_INSIDE', label: 'Isolate' },
          { value: 'SHOW_OUTSIDE', label: 'Hide' },
        ],
      },
      ui: { tooltip: 'Isolate: show only points within tolerance. Hide: remove points within tolerance.', group: 'Point Cloud Clipping', order: 1 },
    });
  }

  if (!propertyRegistry.isRegistered('floor_heatmap_clip_tolerance')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'floor_heatmap_clip_tolerance',
      name: 'Clip Tolerance',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0.01,
      annotationTypes: [AnnotationType.FLOOR],
      description: 'Distance from walls for clipping (meters)',
      validation: { min: 0.001, max: 20.0, step: 0.01 },
      ui: { tooltip: 'Distance from walls to use for isolate/hide clipping', group: 'Point Cloud Clipping', order: 2, decimals: 3, useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE },
    });
  }
}

registerFloorProperties();

export function getFloorPlanePositionProperty(floorId: string): ReactiveProperty<Coordinate> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_plane_position', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
}

/**
 * Whether `floorId` has an explicitly stored floor-plane position (i.e. an
 * actual detected/edited floor), as opposed to the registry default. Lets
 * callers tell "this group has a floor" apart from "never set".
 */
export function hasFloorPlanePosition(floorId: string): boolean {
  const service = getGlobalPropertyService();
  return service.hasStoredValue('floor_plane_position', floorId, PropertyType.PROJECT_GLOBAL);
}

export function getFloorPlaneNormalProperty(floorId: string): ReactiveProperty<Coordinate> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_plane_normal', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
}

export function getCeilingPlanePositionProperty(floorId: string): ReactiveProperty<Coordinate> {
  const service = getGlobalPropertyService();
  return service.getProperty('ceiling_plane_position', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
}

/**
 * True only when a ceiling plane was explicitly stored for this floor. The
 * property default is `{0,0,0}`, which is a valid coordinate — so reading the
 * value alone can't tell "has a ceiling" from "never set". Callers that derive
 * wall heights must use this to avoid treating an unset ceiling as z=0 (which
 * would collapse the wall to zero height).
 */
export function hasCeilingPlanePosition(floorId: string): boolean {
  const service = getGlobalPropertyService();
  return service.hasStoredValue('ceiling_plane_position', floorId, PropertyType.PROJECT_GLOBAL);
}

export function getCeilingPlaneNormalProperty(floorId: string): ReactiveProperty<Coordinate> {
  const service = getGlobalPropertyService();
  return service.getProperty('ceiling_plane_normal', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
}

// ─── Floor footprint (per-floor XY corners) ─────────────────────────

/** The floor's own footprint: 4 XY corners (z carries the floor elevation). */
export type FloorFootprintCorners = [Coordinate, Coordinate, Coordinate, Coordinate];

export function getFloorFootprintCornerProperty(floorId: string, index: number): ReactiveProperty<Coordinate> {
  const service = getGlobalPropertyService();
  return service.getProperty(`floor_footprint_corner_${index}`, floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
}

/**
 * Load a floor's own footprint corners, or null when never stored (all four
 * corners at the {0,0,0} default). Consumers fall back to the project-global
 * detection area, then scene bounds — the pre-footprint behavior.
 */
export function loadFloorFootprint(floorId: string): FloorFootprintCorners | null {
  const corners: Coordinate[] = [];
  let allZero = true;
  for (let i = 0; i < 4; i++) {
    const value = getFloorFootprintCornerProperty(floorId, i).value;
    corners.push(value);
    if (value.x !== 0 || value.y !== 0 || value.z !== 0) allZero = false;
  }
  return allZero ? null : (corners as FloorFootprintCorners);
}

// ─── 3D mesh visualization getters ──────────────────────────────────

export function getFloorMesh3DEnabledProperty(floorId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_mesh3d_enabled', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<boolean>;
}

export function getFloorMesh3DColorProperty(floorId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_mesh3d_color', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

export function getFloorMesh3DOpacityProperty(floorId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_mesh3d_opacity', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
}

// ─── Heatmap getters ────────────────────────────────────────────────

export function getFloorWallSourceProperty(floorId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_wall_source', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

export function getFloorHeatmapEnabledProperty(floorId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_heatmap_enabled', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<boolean>;
}

export function getFloorHeatmapGradientProperty(floorId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_heatmap_gradient', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

export function getFloorHeatmapMaxDistanceProperty(floorId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_heatmap_max_distance', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
}

export function getFloorHeatmapHeightToleranceProperty(floorId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_heatmap_height_tolerance', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
}

export function getFloorHeatmapClipEnabledProperty(floorId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_heatmap_clip_enabled', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<boolean>;
}

export function getFloorHeatmapClipMethodProperty(floorId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_heatmap_clip_method', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

export function getFloorHeatmapClipToleranceProperty(floorId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty('floor_heatmap_clip_tolerance', floorId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
}
